import {
  Fragment,
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import styled, { css } from "styled-components";

import { AnchoredPopup, useAnchoredPopupAnchor } from "../../AnchoredPopup";
import { Checkbox } from "../../Checkbox";
import { FieldContextProvider, useFieldControl } from "../../Field/FieldContext";
import { IconButton } from "../../IconButton";
import { ChevronIcon as ChevronGlyph } from "../../../icons/glyphs";
import {
  isNestedInteractiveTarget,
  NESTED_INTERACTIVE_SELECTOR,
} from "../../../utils/nestedInteractive";
import { isRenderableNode } from "../../../utils/reactNode";
import { Text, type TextLabelStyle, type TextVariantDefinition } from "../../Text";
import { TRUNCATE_ATTR, TruncatedText } from "../../TruncatedText";
import {
  controlFocusRing,
  controlRadius,
  controlTransition,
  inlineElementHeight,
  inlineElementIndicatorSize,
} from "../../foundations/controlStyles";
import type { DropdownItem } from "../../foundations/option";
import { type FloatingPopoverPlacement } from "../../foundations/popover";
import { yScrollbarStyles } from "../../foundations/scrollbar";
import { DropdownSearchField } from "../DropdownSearchField";
import { useDropdownOpenChange, type DropdownOpenChangeHandler } from "../dropdownOpenChange";
import { defaultDropdownFilterOption } from "../dropdownSearch";
import {
  dropdownTriggerGap,
  dropdownTriggerPadding,
  dropdownTriggerTextVariant,
  resolveDropdownMenuWidth,
  type DropdownMenuWidth,
  type DropdownMode,
} from "../common";

export type MultiSelectDropdownOption = DropdownItem & {
  count?: number;
  /** 연속한 같은 그룹 앞에 선택할 수 없는 제목을 표시한다. 검색도 그룹 이름을 포함한다. */
  group?: { value: string; label: string };
  /** 상위 선택에 포함된 값의 읽기 전용 체크 표시. selectedValues와 선택 개수에는 더하지 않는다. */
  included?: boolean;
};

export type MultiSelectDropdownMenuAlign = "left" | "right";
export type MultiSelectDropdownMenuWidth = DropdownMenuWidth;
export type MultiSelectDropdownPlacement = Extract<
  FloatingPopoverPlacement,
  "top-start" | "top-end" | "bottom-start" | "bottom-end"
>;

/** Trigger density. `md` (default) is the standard control height; `sm` is a compact trigger
 * (shorter + tighter vertical padding + smaller label; side padding stays on the shared
 * controlPadX scale) for dense filter bars. Only the trigger changes —
 * the menu/options keep the same size. */
export type MultiSelectDropdownSize = "sm" | "md";
/**
 * Semantic display mode of the built-in trigger summary.
 *
 * - `field` (default): shows only the selected value/count while retaining the labelled
 *   accessible name, and keeps the closed trigger neutral.
 * - `facet`: shows `label: value/count` and emphasizes an active selection — for filter facets.
 *
 * `renderSelectedValue` is an orthogonal content extension for custom tag/chip bars.
 */
export type MultiSelectDropdownMode = DropdownMode;

export type MultiSelectDropdownOptionRenderContext = {
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
};

export type MultiSelectDropdownSelectedValueRenderContext = {
  /** Removes this value without opening or closing the popup. */
  remove: () => void;
  /** `true` when either the dropdown or this option is disabled. */
  disabled: boolean;
};

export type MultiSelectDropdownFilterOption<
  TOption extends MultiSelectDropdownOption = MultiSelectDropdownOption,
> = (option: TOption, query: string) => boolean;

export type MultiSelectDropdownProps<
  TOption extends MultiSelectDropdownOption = MultiSelectDropdownOption,
> = {
  /** Trigger id. Inside Field, the Field id is the single source of truth. */
  id?: string;
  /** Standalone trigger fallback and accessible/popup label. Compose with Field for a visible title. */
  label: string;
  options: readonly TOption[];
  selectedValues: readonly string[];
  onSelectionChange: (selectedValues: readonly string[]) => void;
  /** 실제 popup open 상태가 바뀐 뒤 호출한다. 최초 mount와 callback identity 변경만으로는 호출하지 않는다. */
  onOpenChange?: DropdownOpenChangeHandler;
  /** option의 checkbox와 padding을 포함한 행 전체의 pointer hover가 바뀔 때 호출한다. */
  onOptionHoverChange?: (option: TOption | null) => void;
  placeholder?: string;
  emptyMessage?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  noResultsMessage?: string;
  /**
   * 선택 전체 초기화 action의 가시 문구와 accessible name.
   * 미지정 시 일반 메뉴는 `Clear`, 검색 메뉴는 `Clear all` 계약을 유지한다.
   */
  clearSelectionLabel?: string;
  /**
   * 선택 가능한 option이 일부만 선택된 경우 popup header에 노출할 전체 선택 action 문구.
   * 미지정 consumer는 기존의 선택 초기화 action만 유지한다.
   */
  selectAllLabel?: string;
  filterOption?: MultiSelectDropdownFilterOption<TOption>;
  /** When set, an emphasized `(*)` marker is shown in the placeholder until a value is chosen. */
  required?: boolean;
  disabled?: boolean;
  /** Semantic built-in mode. Custom selected-value content remains controlled by `renderSelectedValue`. */
  mode?: MultiSelectDropdownMode;
  /** trigger가 컨테이너 폭을 채운다(폼 필드 정렬용). 기본은 content-width. `BaseInput.fullWidth` 관례와 동일. */
  fullWidth?: boolean;
  className?: string;
  style?: CSSProperties;
  closeOnSelect?: boolean;
  menuAlign?: MultiSelectDropdownMenuAlign;
  /**
   * 메뉴 폭. CSS 길이 또는 `"matchTrigger"`를 받는다. 메뉴는 portal의 fixed overlay라 `%`는
   * trigger가 아닌 viewport 기준이므로 trigger와 맞추려면 `"matchTrigger"`를 사용한다.
   */
  menuWidth?: MultiSelectDropdownMenuWidth;
  maxMenuHeight?: string;
  /** Preferred popup placement. AnchoredPopup may flip it to keep the menu inside the viewport. */
  placement?: MultiSelectDropdownPlacement;
  size?: MultiSelectDropdownSize;
  /**
   * Replaces the default label/description/count region while C6 keeps the option row,
   * Checkbox, disabled handling, keyboard behavior, and selection semantics.
   *
   * A click inside the custom content toggles the containing option by default. A custom click
   * handler can call `event.preventDefault()` to cancel that row toggle. To perform custom work
   * and still toggle, prevent the default first and then call `context.onToggle()` exactly once.
   */
  renderOptionContent?: (
    option: TOption,
    context: MultiSelectDropdownOptionRenderContext,
  ) => ReactNode;
  /**
   * Renders each selected value while C6 keeps selected-value order, keys, trigger button,
   * chevron, focus, disabled, ARIA, and responsive overflow contracts. C6 measures each rendered
   * value and collapses only the values that do not fit into a `+N` summary. The default
   * placeholder/summary is used when there is no selection, a selected value cannot be resolved
   * from `options`, or any rendered value is empty.
   * Selected-value nodes use a block-safe `TruncatedText` wrapper; mount one
   * `TruncationTooltipHost` at the application root to restore clipped labels on hover.
   *
   * Interactive descendants are layered as siblings of the trigger button, so semantic remove
   * buttons remain valid HTML instead of becoming nested `<button>` elements.
   */
  renderSelectedValue?: (
    option: TOption,
    context: MultiSelectDropdownSelectedValueRenderContext,
  ) => ReactNode;
};

const defaultEmptyMessage = "No options.";
const defaultNoResultsMessage = "No results.";
const defaultSearchPlaceholder = "Search...";
const defaultMenuWidth = "min(22rem, calc(100vw - 2rem))";
const defaultMaxMenuHeight = "20rem";

const boldWeight = "var(--c6-typography-weight-bold)";
const regularWeight = "var(--c6-typography-weight-regular)";
const mutedColor = "var(--c6-color-text-muted)";

// SingleSelectDropdown과 동일하게 선택된 facet의 "label:"은 muted로 낮추고,
// value/count만 trigger의 accent 색을 상속한다.
const defaultTriggerLabelStyle = {
  prefix: {
    as: "span",
    style: { color: mutedColor, fontWeight: regularWeight },
  },
} satisfies TextLabelStyle;
const disabledTriggerLabelStyle = {
  prefix: {
    as: "span",
    style: { color: "inherit", fontWeight: regularWeight },
  },
} satisfies TextLabelStyle;

const menuTitleTextVariant = {
  as: "span",
  style: {
    display: "block",
    minWidth: 0,
    overflow: "hidden",
    color: "var(--c6-color-text-muted)",
    fontSize: "var(--c6-typography-size-caption)",
    fontWeight: boldWeight,
    lineHeight: "var(--c6-typography-line-height-snug)",
    textOverflow: "ellipsis",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
} satisfies TextVariantDefinition;

const emptyTextVariant = {
  as: "span",
  style: {
    color: "var(--c6-color-text-muted)",
    fontSize: "var(--c6-typography-size-body)",
    lineHeight: "var(--c6-typography-line-height-normal)",
  },
} satisfies TextVariantDefinition;

const optionLabelTextVariant = {
  as: "span",
  style: {
    display: "block",
    minWidth: 0,
    overflow: "hidden",
    color: "inherit",
    fontSize: "var(--c6-typography-size-body)",
    fontWeight: regularWeight,
    lineHeight: "var(--c6-typography-line-height-tight)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
} satisfies TextVariantDefinition;

const optionDescriptionTextVariant = {
  as: "span",
  style: {
    display: "block",
    minWidth: 0,
    overflow: "hidden",
    color: "var(--c6-color-text-muted)",
    fontSize: "var(--c6-typography-size-caption)",
    lineHeight: "var(--c6-typography-line-height-tight)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
} satisfies TextVariantDefinition;

const optionCountTextVariant = {
  as: "span",
  style: {
    color: "var(--c6-color-text-muted)",
    fontSize: "var(--c6-typography-size-caption)",
    fontWeight: boldWeight,
    lineHeight: "1",
  },
} satisfies TextVariantDefinition;

const Root = styled.div<{ $fullWidth: boolean }>`
  position: relative;
  display: ${({ $fullWidth }) => ($fullWidth ? "flex" : "inline-flex")};
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  max-width: 100%;
  min-width: 0;
`;

const Trigger = styled(IconButton)<{
  $open: boolean;
  $selected: boolean;
  $size: MultiSelectDropdownSize;
  $fullWidth: boolean;
}>`
  display: ${({ $fullWidth }) => ($fullWidth ? "flex" : "inline-flex")};
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  max-width: 100%;
  min-width: 0;
  min-height: ${({ $size }) => inlineElementHeight[$size]};
  align-items: center;
  justify-content: space-between;
  gap: ${({ $size }) => dropdownTriggerGap($size)};
  /* 패딩·gap 은 SingleSelect 트리거와 공유한다(common.ts) — 세로는 min-height 이하라 계약 높이에 바인딩. */
  padding: ${({ $size }) => dropdownTriggerPadding($size)};
  ${controlRadius}
  border: 1px solid
    ${({ $open, $selected, theme }) =>
    $open || $selected ? theme.color.border.selected : theme.color.border.subtle};
  background-color: ${({ $open, theme }) =>
    $open ? theme.color.bg.surfaceRaisedHigh : theme.color.bg.surfaceSunken};
  color: ${({ $open, $selected, theme }) =>
    $open || $selected ? theme.color.text.accent : theme.color.text.default};
  font: inherit;
  ${controlTransition("background-color", "border-color", "color")}

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.color.border.selected};
    background-color: ${({ theme }) => theme.color.bg.surfaceRaisedHigh};
    color: ${({ theme }) => theme.color.text.accent};
  }

  &:focus-visible {
    ${controlFocusRing()}
  }

  &:disabled {
    border-color: ${({ theme }) => theme.color.border.disabled};
    background-color: ${({ theme }) => theme.color.bg.control.disabled};
    color: ${({ theme }) => theme.color.text.disabled};
    cursor: not-allowed;
  }
`;

// Custom selected values can contain their own remove buttons. They therefore cannot be children
// of Trigger (a native button). This grid overlays a presentation layer and the real open button as
// siblings. TruncatedText markers receive hover for overflow restoration and forward non-interactive
// clicks to the real trigger; semantic interactive descendants remain independent controls.
const CustomTriggerShell = styled.div<{
  $size: MultiSelectDropdownSize;
  $fullWidth: boolean;
}>`
  display: grid;
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  max-width: 100%;
  height: ${({ $size }) => inlineElementHeight[$size]};
  min-width: 0;
`;

const CustomTriggerButton = styled(Trigger)`
  width: 100%;
  height: 100%;
  min-height: 0;
  grid-area: 1 / 1;
`;

const CustomTriggerContent = styled.div<{
  $open: boolean;
  $selected: boolean;
  $disabled: boolean;
  $size: MultiSelectDropdownSize;
}>`
  z-index: 1;
  box-sizing: border-box;
  display: grid;
  height: 100%;
  min-width: 0;
  grid-area: 1 / 1;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: ${({ $size }) => dropdownTriggerGap($size)};
  padding: ${({ $size }) => dropdownTriggerPadding($size)};
  overflow: hidden;
  color: ${({ $disabled, $open, $selected, theme }) =>
    $disabled
      ? theme.color.text.disabled
      : $open || $selected
        ? theme.color.text.accent
        : theme.color.text.default};
  pointer-events: none;

  & [${TRUNCATE_ATTR}] {
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
    pointer-events: auto;
  }
`;

const SelectedValueSlot = styled.div<{ $disabled: boolean }>`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;

  & > * {
    min-width: 0;
    max-width: 100%;
    flex: 0 1 auto;
  }

  & > :not(:is(${NESTED_INTERACTIVE_SELECTOR})) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ${({ $disabled }) =>
    !$disabled
      ? css`
          & :is(${NESTED_INTERACTIVE_SELECTOR}) {
            pointer-events: auto;
          }
        `
      : ""}
`;

const SelectedValueOverflowList = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  align-items: center;
  gap: 0.25rem;
  overflow: hidden;
  white-space: nowrap;
`;

const SelectedValueOverflowItem = styled.div<{
  $visible: boolean;
  $shrinkToFit: boolean;
}>`
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
  flex: ${({ $shrinkToFit }) => ($shrinkToFit ? "0 1 auto" : "0 0 auto")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  & > :not(:is(${NESTED_INTERACTIVE_SELECTOR})) {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:focus-within,
  &:focus-within > * {
    overflow: visible;
  }

  ${({ $visible }) =>
    !$visible
      ? css`
          position: absolute;
          top: 0;
          left: 0;
          width: max-content;
          visibility: hidden;
          pointer-events: none;
        `
      : ""}
`;

const SelectedValueOverflowCount = styled.span<{ $measure: boolean }>`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.color.text.muted};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.bold};
  font-variant-numeric: tabular-nums;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  white-space: nowrap;

  ${({ $measure }) =>
    $measure
      ? css`
          position: absolute;
          top: 0;
          left: 0;
          width: max-content;
          visibility: hidden;
          pointer-events: none;
        `
      : ""}
`;

const Chevron = styled(ChevronGlyph)<{ $open: boolean; $size: MultiSelectDropdownSize }>`
  flex: 0 0 auto;
  width: ${({ $size }) => inlineElementIndicatorSize[$size]};
  height: ${({ $size }) => inlineElementIndicatorSize[$size]};
  transition: transform 120ms ease;

  ${({ $open }) =>
    $open
      ? css`
          transform: rotate(180deg);
        `
      : ""}
`;

// 메뉴 오버레이는 AnchoredPopup(portal + fixed + flip/clamp + modalStack)에 위임한다.
// Panel의 기본 표면(surfaceRaised/그림자)을 기존 dropdown 메뉴 외관으로 보존 — 이관은 포지셔닝만
// 바꾸고 resting 외관은 유지(시각 리디자인은 별개).
const MenuPopup = styled(AnchoredPopup)`
  && {
    background-color: ${({ theme }) => theme.color.bg.surface};
    border-color: ${({ theme }) => theme.color.border.default};
    box-shadow: ${({ theme }) => theme.elevation.shadow.overlay};
  }
`;

// Panel(flex column) 안에서 header(auto) + 스크롤 목록(1fr)을 기존 grid 레이아웃 그대로 채운다.
const MenuInner = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
  flex: 1;
`;

const MenuHeader = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.default};
  background-color: ${({ theme }) => theme.color.bg.surfaceRaised};
`;

const SelectionActionButton = styled.button`
  flex: 0 0 auto;
  padding: 0.25rem 0.5rem;
  ${controlRadius}
  border: 0;
  background-color: transparent;
  color: ${({ theme }) => theme.color.text.accent};
  font: inherit;
  font-size: ${({ theme }) => theme.typography.size.body};
  font-weight: ${({ theme }) => theme.typography.weight.action};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.color.bg.surfaceRaisedHigh};
  }

  &:focus-visible {
    ${controlFocusRing()}
  }
`;

const OptionList = styled.div`
  display: grid;
  min-width: 0;
  min-height: 0;
  padding: 0.5rem 0;
  ${yScrollbarStyles}
`;

const EmptyMessage = styled(Text)`
  padding: 0.75rem;
`;

const OptionGroupTitle = styled.div`
  margin-top: 0.25rem;
  padding: 0.5rem 0.75rem;
  border-block: 1px solid ${({ theme }) => theme.color.border.default};
  background: ${({ theme }) => theme.color.bg.surfaceRaised};
  color: ${({ theme }) => theme.color.text.muted};
`;

const Option = styled.label<{ $selected: boolean; $disabled: boolean; $grouped?: boolean }>`
  display: grid;
  min-width: 0;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  padding-left: ${({ $grouped }) => ($grouped ? "1.25rem" : "0.75rem")};
  color: ${({ $disabled, theme }) =>
    $disabled ? theme.color.text.disabled : theme.color.text.default};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};

  ${({ $selected, theme }) =>
    $selected
      ? css`
          background-color: ${theme.color.bg.surfaceRaised};
        `
      : ""}

  &:hover {
    background-color: ${({ $disabled, theme }) =>
      $disabled ? "transparent" : theme.color.bg.surfaceRaised};
  }
`;

const OptionCheckbox = styled(Checkbox)`
  pointer-events: none;
`;

const OptionText = styled.span<{ $custom: boolean }>`
  display: grid;
  min-width: 0;
  grid-column: ${({ $custom }) => ($custom ? "2 / -1" : "auto")};
  gap: 0.25rem;
`;

const OptionCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.6rem;
  min-height: 1.35rem;
  padding: 0 0.5rem;
  ${controlRadius}
  background-color: ${({ theme }) => theme.color.bg.surfaceRaisedHigh};
  font-variant-numeric: tabular-nums;
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.color.text.danger};
  font-weight: ${({ theme }) => theme.typography.weight.bold};
`;

type ResponsiveSelectedValuesProps = {
  values: readonly {
    key: string;
    fullText: string;
    content: ReactNode;
  }[];
  fallbackFocusRef: RefObject<HTMLButtonElement | null>;
};

function ResponsiveSelectedValues({ values, fallbackFocusRef }: ResponsiveSelectedValuesProps) {
  const [visibleCount, setVisibleCount] = useState(values.length);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const overflowMeasureRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    const overflowMeasure = overflowMeasureRef.current;
    const itemElements = itemRefs.current.slice(0, values.length);
    if (list == null || overflowMeasure == null || itemElements.some((item) => item == null)) {
      return;
    }

    const updateVisibleCount = () => {
      const gap = Number.parseFloat(window.getComputedStyle(list).columnGap) || 0;
      const itemWidths = itemElements.map((item) => item?.getBoundingClientRect().width ?? 0);
      const nextVisibleCount = resolveVisibleSelectedValueCount({
        availableWidth: list.getBoundingClientRect().width,
        itemWidths,
        overflowWidth: overflowMeasure.getBoundingClientRect().width,
        gap,
      });
      const activeElement = document.activeElement;
      if (
        activeElement != null &&
        itemElements.slice(nextVisibleCount).some((item) => item?.contains(activeElement) ?? false)
      ) {
        fallbackFocusRef.current?.focus();
      }
      setVisibleCount((currentCount) =>
        currentCount === nextVisibleCount ? currentCount : nextVisibleCount,
      );
    };

    updateVisibleCount();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateVisibleCount);
      return () => window.removeEventListener("resize", updateVisibleCount);
    }

    const resizeObserver = new ResizeObserver(updateVisibleCount);
    resizeObserver.observe(list);
    resizeObserver.observe(overflowMeasure);
    itemElements.forEach((item) => {
      if (item != null) resizeObserver.observe(item);
    });
    return () => resizeObserver.disconnect();
  }, [fallbackFocusRef, values]);

  if (values.length === 0) {
    return null;
  }

  const hiddenCount = Math.max(0, values.length - visibleCount);

  return (
    <SelectedValueOverflowList ref={listRef} data-c6-selected-value-list="">
      {values.map((value, index) => {
        const visible = index < visibleCount;
        return (
          <SelectedValueOverflowItem
            key={value.key}
            ref={(element) => {
              itemRefs.current[index] = element;
            }}
            $visible={visible}
            $shrinkToFit={visible && visibleCount === 1 && hiddenCount > 0}
            data-c6-selected-value-item=""
            aria-hidden={visible ? undefined : "true"}
            inert={visible ? undefined : true}
          >
            <TruncatedText as="div" fullText={value.fullText}>
              {value.content}
            </TruncatedText>
          </SelectedValueOverflowItem>
        );
      })}
      {hiddenCount > 0 ? (
        <SelectedValueOverflowCount
          $measure={false}
          aria-label={`${hiddenCount} more selected options`}
        >
          +{hiddenCount}
        </SelectedValueOverflowCount>
      ) : null}
      <SelectedValueOverflowCount
        ref={overflowMeasureRef}
        $measure
        data-c6-selected-value-overflow-measure=""
        aria-hidden="true"
      >
        +{values.length}
      </SelectedValueOverflowCount>
    </SelectedValueOverflowList>
  );
}

function resolveVisibleSelectedValueCount({
  availableWidth,
  itemWidths,
  overflowWidth,
  gap,
}: {
  availableWidth: number;
  itemWidths: readonly number[];
  overflowWidth: number;
  gap: number;
}): number {
  const fullWidth =
    itemWidths.reduce((total, itemWidth) => total + itemWidth, 0) +
    Math.max(0, itemWidths.length - 1) * gap;
  if (fullWidth <= availableWidth + 0.5) {
    return itemWidths.length;
  }

  let occupiedWidth = overflowWidth;
  let visibleCount = 0;
  for (const itemWidth of itemWidths) {
    const nextOccupiedWidth = occupiedWidth + gap + itemWidth;
    if (nextOccupiedWidth > availableWidth + 0.5) {
      break;
    }
    occupiedWidth = nextOccupiedWidth;
    visibleCount += 1;
  }
  // Keep one selected value recognizable even when its intrinsic width plus `+N` does not fit.
  // That first item becomes the only shrinkable flex item, so its label truncates while `+N`
  // remains visible instead of replacing every value with an anonymous count.
  return itemWidths.length > 0 ? Math.max(1, visibleCount) : 0;
}

export function MultiSelectDropdown<
  TOption extends MultiSelectDropdownOption = MultiSelectDropdownOption,
>({
  id,
  label,
  options,
  selectedValues,
  onSelectionChange,
  onOpenChange,
  onOptionHoverChange,
  placeholder,
  emptyMessage = defaultEmptyMessage,
  searchable = false,
  searchPlaceholder = defaultSearchPlaceholder,
  noResultsMessage = defaultNoResultsMessage,
  clearSelectionLabel,
  selectAllLabel,
  filterOption,
  required = false,
  disabled = false,
  mode = "field",
  fullWidth = false,
  className,
  style,
  closeOnSelect = true,
  menuAlign = "left",
  menuWidth = defaultMenuWidth,
  maxMenuHeight = defaultMaxMenuHeight,
  placement,
  size = "md",
  renderOptionContent,
  renderSelectedValue,
}: MultiSelectDropdownProps<TOption>) {
  const { controlId, disabled: isDisabled } = useFieldControl({
    componentName: "MultiSelectDropdown",
    id,
    disabled,
  });
  const { anchor, open, openFromElement, close: closeAnchor } = useAnchoredPopupAnchor();
  const [searchQuery, setSearchQuery] = useState("");
  const customTriggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  useDropdownOpenChange(open, onOpenChange);
  const selectedValueSet = useMemo(() => new Set(selectedValues), [selectedValues]);
  const optionByValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );
  const optionLabelByValue = useMemo(
    () => new Map(options.map((option) => [option.value, option.label])),
    [options],
  );
  const selectedOptions = useMemo(
    () =>
      selectedValues.reduce<TOption[]>((result, value) => {
        const option = optionByValue.get(value);
        if (option != null) {
          result.push(option);
        }
        return result;
      }, []),
    [optionByValue, selectedValues],
  );
  const { accessibleText: triggerText, visibleMarkup: visibleTriggerMarkup } = buildTriggerLabel({
    label,
    placeholder,
    selectedValues,
    mode,
    optionLabelByValue,
  });
  const showRequiredMarker = required && selectedValues.length === 0;
  const triggerAriaLabel = showRequiredMarker ? `${triggerText} (required)` : triggerText;
  const isEmphasizedSelection = mode === "facet" && selectedValues.length > 0;
  const resolvedPlacement = placement ?? (menuAlign === "right" ? "bottom-end" : "bottom-start");
  const resolvedMenuWidth = resolveDropdownMenuWidth(menuWidth);
  const resolvedClearSelectionLabel = clearSelectionLabel ?? (searchable ? "Clear all" : "Clear");
  const resolvedClearSelectionAriaLabel =
    clearSelectionLabel ?? (searchable ? "Clear all selected options" : undefined);
  const selectableOptions = useMemo(
    () => options.filter((option) => !option.disabled && !option.included),
    [options],
  );
  const allSelectableOptionsSelected =
    selectableOptions.length > 0 &&
    selectableOptions.every((option) => selectedValueSet.has(option.value));
  const canSelectAll =
    selectAllLabel != null && selectableOptions.length > 0 && !allSelectableOptionsSelected;
  const filteredOptions = useMemo(() => {
    if (!searchable) {
      return options;
    }

    const filter =
      filterOption ??
      ((option: TOption, query: string) =>
        defaultDropdownFilterOption(option, query) ||
        (option.group !== undefined && defaultDropdownFilterOption(option.group, query)));
    return options.filter((option) => filter(option, searchQuery));
  }, [filterOption, options, searchable, searchQuery]);

  const close = useCallback(() => {
    closeAnchor();
    setSearchQuery("");
  }, [closeAnchor]);

  useLayoutEffect(() => {
    if (isDisabled) {
      close();
    }
  }, [close, isDisabled]);

  const toggleOption = useCallback(
    (option: TOption) => {
      if (isDisabled || option.disabled || option.included) {
        return;
      }

      const nextSelectedValueSet = new Set(selectedValues);
      if (selectedValueSet.has(option.value)) {
        nextSelectedValueSet.delete(option.value);
      } else {
        nextSelectedValueSet.add(option.value);
      }

      onSelectionChange(
        options
          .filter((currentOption) => nextSelectedValueSet.has(currentOption.value))
          .map((currentOption) => currentOption.value),
      );

      if (closeOnSelect) {
        close();
      }
    },
    [
      close,
      closeOnSelect,
      isDisabled,
      onSelectionChange,
      options,
      selectedValueSet,
      selectedValues,
    ],
  );

  const clearSelection = useCallback(() => {
    if (isDisabled) {
      return;
    }
    onSelectionChange([]);
    if (closeOnSelect) {
      close();
    }
  }, [close, closeOnSelect, isDisabled, onSelectionChange]);

  const selectAllOptions = useCallback(() => {
    onSelectionChange(
      options
        .filter(
          (option) => (!option.disabled && !option.included) || selectedValueSet.has(option.value),
        )
        .map((option) => option.value),
    );
    if (closeOnSelect) {
      close();
    }
  }, [close, closeOnSelect, onSelectionChange, options, selectedValueSet]);

  const selectionAction =
    canSelectAll && selectAllLabel != null ? (
      <SelectionActionButton type="button" aria-label={selectAllLabel} onClick={selectAllOptions}>
        {selectAllLabel}
      </SelectionActionButton>
    ) : selectedValues.length > 0 ? (
      <SelectionActionButton
        type="button"
        aria-label={resolvedClearSelectionAriaLabel}
        onClick={clearSelection}
      >
        {resolvedClearSelectionLabel}
      </SelectionActionButton>
    ) : undefined;

  const removeValue = useCallback(
    (value: string) => {
      if (
        isDisabled ||
        !selectedValueSet.has(value) ||
        (optionByValue.get(value)?.disabled ?? false)
      ) {
        return;
      }

      if (customTriggerRef.current?.parentElement?.contains(document.activeElement) ?? false) {
        customTriggerRef.current?.focus();
      }
      onSelectionChange(selectedValues.filter((selectedValue) => selectedValue !== value));
    },
    [isDisabled, onSelectionChange, optionByValue, selectedValueSet, selectedValues],
  );

  const handleTriggerPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (open) event.stopPropagation();
    },
    [open],
  );

  const handleSelectedValuePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (open) event.stopPropagation();
    },
    [open],
  );

  const handleTriggerClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      if (open) {
        close();
      } else {
        openFromElement(event);
      }
    },
    [close, open, openFromElement],
  );

  const handleSelectedValueClick = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (isNestedInteractiveTarget(event.target, event.currentTarget)) {
      return;
    }

    customTriggerRef.current?.focus();
    customTriggerRef.current?.click();
  }, []);

  const defaultTriggerValue = (
    <Text
      variant={dropdownTriggerTextVariant[size]}
      labelStyle={
        mode === "facet"
          ? isDisabled
            ? disabledTriggerLabelStyle
            : defaultTriggerLabelStyle
          : undefined
      }
    >
      {visibleTriggerMarkup}
      {showRequiredMarker ? (
        <span aria-hidden="true">
          {" ("}
          <RequiredMark>*</RequiredMark>
          {")"}
        </span>
      ) : null}
    </Text>
  );
  const renderedSelectedValues =
    renderSelectedValue != null &&
    selectedOptions.length > 0 &&
    selectedOptions.length === selectedValues.length
      ? selectedOptions.map((option) => ({
          key: option.value,
          fullText: option.label,
          content: renderSelectedValue(option, {
            remove: () => removeValue(option.value),
            disabled: isDisabled || (option.disabled ?? false),
          }),
        }))
      : null;
  const customSelectedValues =
    renderedSelectedValues != null &&
    renderedSelectedValues.every(({ content }) => isRenderableNode(content))
      ? renderedSelectedValues
      : null;

  return (
    <Root className={className} style={style} $fullWidth={fullWidth}>
      {renderSelectedValue == null ? (
        <Trigger
          id={controlId}
          $open={open}
          $selected={isEmphasizedSelection}
          $size={size}
          $fullWidth={fullWidth}
          aria-label={triggerAriaLabel}
          disabled={isDisabled}
          variant="surface"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          // 열려 있을 때만 pointerdown 전파를 끊는다 — 자기 토글-닫힘 시 pointerdown이 document로 새면
          // 메뉴가 먼저 닫히고 이어지는 click이 다시 열어 토글이 깨진다. 닫혀 있을 땐(=새로 여는 트리거)
          // 전파시켜 다른 열린 popover가 이 클릭을 outside-dismiss로 감지하게 둔다(여러 dropdown 누적 방지).
          onPointerDown={handleTriggerPointerDown}
          onClick={handleTriggerClick}
        >
          {defaultTriggerValue}
          <Chevron $open={open} $size={size} />
        </Trigger>
      ) : (
        <CustomTriggerShell $size={size} $fullWidth={fullWidth}>
          <CustomTriggerButton
            id={controlId}
            ref={customTriggerRef}
            $open={open}
            $selected={isEmphasizedSelection}
            $size={size}
            $fullWidth={fullWidth}
            aria-label={triggerAriaLabel}
            disabled={isDisabled}
            variant="surface"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? menuId : undefined}
            onPointerDown={handleTriggerPointerDown}
            onClick={handleTriggerClick}
          />
          {/* Custom selected-value descendants are sub-controls, not the Field's primary trigger. */}
          <FieldContextProvider value={null}>
            <CustomTriggerContent
              $open={open}
              $selected={isEmphasizedSelection}
              $disabled={isDisabled}
              $size={size}
              aria-hidden={customSelectedValues == null ? "true" : undefined}
            >
              <SelectedValueSlot
                $disabled={isDisabled}
                inert={isDisabled}
                onPointerDown={handleSelectedValuePointerDown}
                onClick={handleSelectedValueClick}
              >
                {customSelectedValues != null ? (
                  <ResponsiveSelectedValues
                    values={customSelectedValues}
                    fallbackFocusRef={customTriggerRef}
                  />
                ) : (
                  defaultTriggerValue
                )}
              </SelectedValueSlot>
              <Chevron $open={open} $size={size} aria-hidden="true" />
            </CustomTriggerContent>
          </FieldContextProvider>
        </CustomTriggerShell>
      )}

      {/* Popup descendants include Checkboxes and custom content. Reset the outer Field context so
          they cannot inherit the primary trigger id or group-disabled contract. */}
      <FieldContextProvider value={null}>
        <MenuPopup
          open={open && !isDisabled}
          anchor={anchor}
          onClose={close}
          // Panel은 presentation — 기본은 MenuInner, searchable은 검색 header 밖의 OptionList가
          // menu 시맨틱(+id)을 소유하고 트리거 aria-controls가 그 실제 menu를 가리킨다.
          role="presentation"
          placement={resolvedPlacement}
          offset={4}
          // 검색이 없으면 panel로 focus를 넘겨 Tab으로 옵션에 닿게 한다. 검색이 있으면 SingleSelect와 같은
          // AnchoredPopup firstFocusable 정책으로 검색 input에 진입한다. 첫 옵션은 arrow-nav가 없어 바로 보내지 않는다.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={searchable ? "firstFocusable" : "panel"}
          // 열 때 focus를 팝업으로 넣었으니 닫힐 때(Escape/옵션 선택·clear) 트리거로 되돌린다 — page-level은
          // AnchoredPopup 기본이 복원 안 해 focus가 body로 떨어진다. 바깥 클릭은 미복원(AnchoredPopup 감지).
          returnFocus
          width={resolvedMenuWidth}
          maxWidth="calc(100vw - 2rem)"
          maxHeight={maxMenuHeight}
        >
          <MenuInner
            id={searchable ? undefined : menuId}
            role={searchable ? undefined : "menu"}
            aria-label={searchable ? undefined : label}
          >
            {searchable ? (
              <DropdownSearchField
                value={searchQuery}
                onValueChange={setSearchQuery}
                placeholder={searchPlaceholder}
                action={selectionAction}
              />
            ) : (
              <MenuHeader>
                <Text variant={menuTitleTextVariant}>{label}</Text>
                {selectionAction}
              </MenuHeader>
            )}

            <OptionList
              id={searchable ? menuId : undefined}
              role={searchable ? "menu" : undefined}
              aria-label={searchable ? label : undefined}
            >
              {options.length === 0 ? (
                <EmptyMessage variant={emptyTextVariant}>{emptyMessage}</EmptyMessage>
              ) : filteredOptions.length === 0 ? (
                <EmptyMessage variant={emptyTextVariant}>{noResultsMessage}</EmptyMessage>
              ) : (
                filteredOptions.map((option, index) => {
                  const checked = selectedValueSet.has(option.value) || Boolean(option.included);
                  const optionDisabled = isDisabled || Boolean(option.disabled || option.included);
                  const onToggle = () => toggleOption(option);
                  const context: MultiSelectDropdownOptionRenderContext = {
                    selected: checked,
                    disabled: optionDisabled,
                    onToggle,
                  };
                  return (
                    <Fragment key={option.value}>
                      {option.group &&
                      option.group.value !== filteredOptions[index - 1]?.group?.value ? (
                        <OptionGroupTitle
                          role="presentation"
                          data-option-group={option.group.value}
                          id={`${menuId}-group-${encodeURIComponent(option.group.value)}`}
                        >
                          <Text variant={menuTitleTextVariant}>{option.group.label}</Text>
                        </OptionGroupTitle>
                      ) : null}
                      <Option
                        $grouped={option.group !== undefined}
                        $selected={checked}
                        $disabled={optionDisabled}
                        aria-disabled={optionDisabled || undefined}
                        onMouseEnter={() => onOptionHoverChange?.(option)}
                        onMouseLeave={() => onOptionHoverChange?.(null)}
                      >
                        <OptionCheckbox
                          checked={checked}
                          disabled={optionDisabled}
                          aria-describedby={
                            option.group
                              ? `${menuId}-group-${encodeURIComponent(option.group.value)}`
                              : undefined
                          }
                          aria-checked={checked}
                          size="1rem"
                          onChange={onToggle}
                        />
                        <OptionText $custom={renderOptionContent != null}>
                          {renderOptionContent != null ? (
                            renderOptionContent(option, context)
                          ) : (
                            <>
                              <Text variant={optionLabelTextVariant}>{option.label}</Text>
                              {option.description ? (
                                <Text variant={optionDescriptionTextVariant}>
                                  {option.description}
                                </Text>
                              ) : null}
                            </>
                          )}
                        </OptionText>
                        {renderOptionContent == null && typeof option.count === "number" ? (
                          <OptionCount>
                            <Text variant={optionCountTextVariant}>{option.count}</Text>
                          </OptionCount>
                        ) : null}
                      </Option>
                    </Fragment>
                  );
                })
              )}
            </OptionList>
          </MenuInner>
        </MenuPopup>
      </FieldContextProvider>
    </Root>
  );
}

function buildTriggerLabel({
  label,
  placeholder,
  selectedValues,
  mode,
  optionLabelByValue,
}: {
  label: string;
  placeholder?: string;
  selectedValues: readonly string[];
  mode: MultiSelectDropdownMode;
  optionLabelByValue: ReadonlyMap<string, string>;
}): { accessibleText: string; visibleMarkup: string } {
  if (selectedValues.length === 0) {
    const placeholderText = placeholder ?? label;
    return {
      accessibleText:
        mode === "field" && placeholder != null ? `${label}: ${placeholder}` : placeholderText,
      visibleMarkup: placeholderText,
    };
  }

  if (selectedValues.length === 1) {
    const [selectedValue] = selectedValues;
    const selectedLabel = optionLabelByValue.get(selectedValue) ?? selectedValue;
    return {
      accessibleText: `${label}: ${selectedLabel}`,
      visibleMarkup:
        mode === "facet"
          ? `<prefix>${label}: </prefix><value>${selectedLabel}</value>`
          : selectedLabel,
    };
  }

  return {
    accessibleText: `${label}: ${selectedValues.length}`,
    visibleMarkup:
      mode === "facet"
        ? `<prefix>${label}: </prefix><value>${selectedValues.length}</value>`
        : `${selectedValues.length} selected`,
  };
}

MultiSelectDropdown.displayName = "MultiSelectDropdown";
